import { useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { useChatThreads } from './queries'

/**
 * Pill that appears at the top of any AppShell-mounted page when the
 * URL carries `?fromChat=<threadId>`. Clicking it returns the user to
 * `/chatbot?thread=<threadId>`, which ChatbotPage reads on mount and
 * uses to pre-select the original thread - so a "look at this invoice"
 * → "back to chat" round trip takes one click instead of re-finding
 * the thread in the sidebar.
 *
 * Mounted once in AppShell so every routed page automatically picks
 * it up; per-page wiring is just "do nothing" - the param-aware
 * banner takes care of itself.
 *
 * Hides itself on the chat page and on bare /login etc. routes (no
 * AppShell on the latter, so it's never rendered there anyway).
 */
export function BackToChatBanner() {
  const [params] = useSearchParams()
  const location = useLocation()
  const threadId = params.get('fromChat')
  const messageId = params.get('fromMsg')
  const threadsQ = useChatThreads()

  const thread = useMemo(() => {
    if (!threadId) return null
    return threadsQ.data?.threads.find((t) => t.id === threadId) ?? null
  }, [threadId, threadsQ.data?.threads])

  // Don't render on the chat page itself - clicking the link from chat
  // would be a noop and the pill would visually duplicate the header.
  if (!threadId) return null
  if (location.pathname.startsWith('/chatbot')) return null

  // Build the return URL. ChatbotPage reads `?thread=<id>` on mount.
  // We also forward `fromMsg` so a future "scroll to message" deep-link
  // is one ChatbotPage change away - no need to re-thread it through
  // every citation source again.
  const search = new URLSearchParams({ thread: threadId })
  if (messageId) search.set('msg', messageId)
  const back = `/chatbot?${search.toString()}`

  // Use the thread title when we have it; fall back to "Smart Chat" so
  // the pill doesn't render an empty quote when the thread list is
  // still loading or the thread was deleted between hops.
  const title = thread?.title?.trim() || 'Smart Chat'

  return (
    <div className="mb-4 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
      <MessageSquare className="size-4 shrink-0 text-primary" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        Opened from chat:{' '}
        <span className="font-medium text-foreground">{title}</span>
      </span>
      <Link
        to={back}
        className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to chat
      </Link>
    </div>
  )
}
